import { Graphics } from 'pixi.js';
import { buildingType } from '../data/buildings.ts';
import { hash2 } from '../core/math.ts';
import { Terrain, WallKind } from '../sim/types.ts';
import type { TileMap } from '../sim/map.ts';
import type { World } from '../sim/world.ts';
import type { TextureBank } from './textures.ts';
import type { Camera } from './camera.ts';

type Point = [number, number];
const dry = (t: number) => t === Terrain.AshPlain || t === Terrain.Forest || t === Terrain.Ruin || t === Terrain.Road;
const STEP: readonly Point[] = [[0, 1], [-1, 0], [1, 0], [0, -1]];

/** Decorative access paths use walkable ground only. They confer no road bonus. */
export function doorstepPath(map: TileMap, x: number, y: number, radius = 8): Point[] {
  x = Math.floor(x); y = Math.floor(y);
  const clear = (xx: number, yy: number): boolean => {
    if (!map.inBounds(xx, yy)) return false;
    const k = map.idx(xx, yy);
    return dry(map.terrain[k]) && map.wall[k] === WallKind.None && map.building[k] < 0 &&
      !map.resourceSolid?.[k] && map.rubble[k] === 0;
  };
  if (!clear(x, y)) return [];
  const start = map.idx(x, y), queue = [start], previous = new Map<number, number>([[start, -1]]);
  let target = -1;
  for (let head = 0; head < queue.length; head++) {
    const k = queue[head], xx = k % map.w, yy = Math.floor(k / map.w);
    if (map.terrain[k] === Terrain.Road) { target = k; break; }
    for (const [dx, dy] of STEP) {
      const nx = xx + dx, ny = yy + dy, next = map.idx(nx, ny);
      if (Math.abs(nx - x) + Math.abs(ny - y) > radius || previous.has(next) || !clear(nx, ny)) continue;
      previous.set(next, k); queue.push(next);
    }
  }
  if (target < 0) return [];
  const path: Point[] = [];
  for (let k = target; k >= 0; k = previous.get(k) ?? -1) path.push([k % map.w + .5, Math.floor(k / map.w) + .5]);
  return path.reverse();
}

/** Corners stay inside their clear tile; the widest fringe is under one tile. */
function accessLine(g: Graphics, path: Point[], doorY: number): void {
  g.moveTo(path[0][0], doorY + .08);
  for (let i = 0; i < path.length; i++) {
    const p = path[i], before = path[i - 1], after = path[i + 1];
    if (!before || !after) { g.lineTo(...p); continue; }
    const entry: Point = [p[0] + (before[0] - p[0]) * .28, p[1] + (before[1] - p[1]) * .28];
    const exit: Point = [p[0] + (after[0] - p[0]) * .28, p[1] + (after[1] - p[1]) * .28];
    g.lineTo(...entry).quadraticCurveTo(p[0], p[1], exit[0], exit[1]);
  }
}

function wornAccess(g: Graphics, path: Point[], doorY: number, soil: number): void {
  // Several faint fringes blend into turf; only the narrow tread is worn bare.
  for (const [width, alpha] of [[.92,.018],[.74,.028],[.55,.045],[.36,.065],[.18,.095]]) {
    accessLine(g, path, doorY);
    g.stroke({ color: soil, width, alpha, cap: 'round', join: 'round' });
  }
  // Small, uneven patches break the ruler-straight edge without drifting off
  // the walkable center tiles. The same position always receives the same wear.
  for (const [x, y] of path) {
    const seed = hash2(Math.floor(x), Math.floor(y), 679);
    if (seed > .42) {
      const side = seed > .72 ? 1 : -1;
      g.ellipse(x + side * .15, y + (seed - .5) * .2, .21 + seed * .07, .16 + seed * .04)
        .fill({ color: soil, alpha: .055 });
    }
    if (seed < .58) {
      const px = x + (hash2(Math.floor(x), Math.floor(y), 683) - .5) * .32;
      const py = y + (seed - .5) * .38;
      g.ellipse(px, py, .015 + seed * .015, .011 + seed * .01).fill({ color: 0xc3ae82, alpha: .23 });
    }
  }
}

/** Clip worn soil to dry tiles, including at riverbanks and against neighbours. */
function clipTile(points: Point[], x: number, y: number): Point[] {
  for (const [axis, edge, sign] of [[0,x,1],[0,x+1,-1],[1,y,1],[1,y+1,-1]]) {
    const input = points; points = [];
    for (let i = 0; i < input.length; i++) {
      const a = input[i], b = input[(i + 1) % input.length];
      const insideA = (a[axis] - edge) * sign >= 0, insideB = (b[axis] - edge) * sign >= 0;
      if (insideA) points.push(a);
      if (insideA !== insideB) {
        const t = (edge - a[axis]) / (b[axis] - a[axis]);
        points.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
      }
    }
    if (!points.length) break;
  }
  return points;
}

/** Cached world-space soil, access tracks and shared daylight shadows. */
export class SettlementDressing {
  readonly ground = new Graphics();
  readonly shadows = new Graphics();
  private world: World | null = null;
  private key = '';

  sync(w: World, bank: TextureBank, cam: Camera): void {
    const bs = w.buildings;
    // Clearing siege rubble opens ground without changing terrainVersion.
    let key = `${w.map.terrainVersion}:${w.map.wallVersion}:${w.rubbleCount}:${w.config.biome}:`;
    for (let i = 0; i < bs.slots.high; i++) if (bs.isAlive(i)) {
      key += `${i},${bs.typeId[i]},${bs.tx[i]},${bs.ty[i]},${Number(bs.build[i] >= 1)},${bank.buildings[bs.typeId[i]].uid};`;
    }
    if (w !== this.world || key !== this.key) {
      this.world = w; this.key = key; this.bake(w, bank);
    }
    for (const layer of [this.ground, this.shadows]) {
      layer.position.set(cam.worldToScreenX(0), cam.worldToScreenY(0));
      layer.scale.set(cam.effectiveZoom);
    }
  }

  private bake(w: World, bank: TextureBank): void {
    const ground = this.ground.clear(), shadows = this.shadows.clear(), bs = w.buildings, map = w.map;
    const snowy = w.config.biome === 'ice';
    const soil = snowy ? 0x89938d : 0x8d7959;
    // Tracks are laid before the yards so their meeting point has no hard seam.
    for (let i = 0; i < bs.slots.high; i++) if (bs.isAlive(i)) {
      const def = buildingType(bs.typeId[i]), x = bs.tx[i] + def.w / 2, y = bs.ty[i] + def.h;
      const path = doorstepPath(map, x, y);
      if (path.length > 1) wornAccess(ground, path, y, soil);
    }
    for (let i = 0; i < bs.slots.high; i++) if (bs.isAlive(i)) {
      const type = bs.typeId[i], def = buildingType(type), geometry = bank.buildingGeometry[type];
      const tx = bs.tx[i], ty = bs.ty[i], cx = tx + def.w / 2, front = ty + def.h;
      const base: Point[] = geometry.groundFootprint.map(p => [cx + (p[0] - geometry.groundCenterX) / 32, front + (p[1] - geometry.groundFrontY) / 32]);
      const cy = base.reduce((sum,p) => sum+p[1],0) / base.length;
      const rx = Math.max(.35, (Math.max(...base.map(p=>p[0]))-Math.min(...base.map(p=>p[0]))) / 2);
      const ry = Math.max(.25, (front-Math.min(...base.map(p=>p[1]))) / 2);
      for (let ring = 0; ring < 4; ring++) {
        const radius = 1.45 - ring * .13;
        const outline: Point[] = Array.from({length:16}, (_,j) => {
          const a = j * Math.PI / 8, wobble = .91 + hash2(j, i, 431) * .15;
          return [cx + Math.cos(a) * (rx * radius + .12) * wobble, cy + Math.sin(a) * (ry * radius + .18) * wobble];
        });
        let painted = false;
        for (let yy = ty - 1; yy <= front + 1; yy++) for (let xx = tx - 1; xx <= tx + def.w + 1; xx++) {
          if (!map.inBounds(xx, yy)) continue;
          const k = map.idx(xx, yy);
          if (!dry(map.terrain[k]) || map.wall[k] || map.resourceSolid?.[k] || map.rubble[k] ||
            (map.building[k] >= 0 && map.building[k] !== i)) continue;
          const points = clipTile(outline, xx, yy);
          if (points.length >= 3) { ground.poly(points.flat()); painted = true; }
        }
        // All clipped tiles in a ring share one fill. Their footprints do not
        // overlap, so alpha stays unchanged while the GPU sees fewer paths.
        if (painted) ground.fill({color:soil,alpha:.08 + ring*.026});
      }
      if (bs.build[i] < 1) continue;
      const height = Math.max(.35, (geometry.groundFrontY - geometry.bounds.y0) / 32);
      const compression = Math.min(1, 3.4 / height);
      // Flatten the actual roof/body outline into the common south-east light
      // direction. Extruding the whole lot made every shadow a large diamond,
      // including yards which do not stand up and cannot cast one.
      const cast: Point[] = geometry.silhouette?.length >= 3 ? geometry.silhouette.map(([x,y]): Point => {
        const rise = Math.max(0, (geometry.groundFrontY - y) / 32) * compression;
        return [cx + (x - geometry.groundCenterX) / 32 + rise * .24, front + rise * .135 - .025];
      }) : base;
      const shadowCx = (Math.min(...cast.map(p => p[0])) + Math.max(...cast.map(p => p[0]))) / 2;
      const shadowCy = (Math.min(...cast.map(p => p[1])) + Math.max(...cast.map(p => p[1]))) / 2;
      const shadowRx = Math.max(.2, Math.max(...cast.map(p => p[0])) - shadowCx);
      const shadowRy = Math.max(.12, Math.max(...cast.map(p => p[1])) - shadowCy);
      for (const [spread, alpha] of [[.105,.018],[.065,.026],[.03,.035],[0,.065]]) {
        const soft = cast.map(([x,y]): Point => [shadowCx + (x - shadowCx) * (1 + spread / shadowRx),
          shadowCy + (y - shadowCy) * (1 + spread / shadowRy)]);
        shadows.poly(soft.flat()).fill({color:0x18221e,alpha});
      }
      shadows.poly(base.flat()).fill({color:0x18221e,alpha:.1});
    }
  }
}
